import { isEmpty } from 'lodash';
import { QUESTION_NEST_PEEQ } from 'data/nodeTypes';

export function getNestingCriterion(audience, criterionKey) {
  return audience.criteria.find(
    ({ key, style, features }) =>
      style === QUESTION_NEST_PEEQ &&
      (key === criterionKey ||
        features.some(({ key: featureKey }) => featureKey === criterionKey)),
  );
}

export function getNestingCriterionByAudienceRefId({
  audiences,
  audienceRefId,
  criterionKey,
}) {
  const audience = audiences.find((item) => item.refId === audienceRefId);

  return getNestingCriterion(audience, criterionKey);
}

export function getFlattenedCriteriaPerAudience(audience) {
  return audience.criteria.reduce((accumulator, criterion) => {
    if (criterion.style === QUESTION_NEST_PEEQ) {
      return [...accumulator, ...criterion.features];
    }

    if (!criterion.values || criterion.values.length === 0) {
      return accumulator;
    }

    return [...accumulator, criterion];
  }, []);
}

export function getAllAudiencesFlattenedCriteria(audiences) {
  return audiences.reduce(
    (accumulator, audience) => [
      ...accumulator,
      ...getFlattenedCriteriaPerAudience(audience),
    ],
    [],
  );
}

export function isTheOnlyOneCriterionOnAudiences(audiences, criterionKey) {
  return (
    getAllAudiencesFlattenedCriteria(audiences).filter(
      (criterion) => criterion.key === criterionKey,
    ).length === 1
  );
}

export function hasCriterionTaxonomyLogic(taxonomyLogic, criterionKey) {
  return !!taxonomyLogic[criterionKey];
}

export function hasCriterionBeenUsedInLoopAndMerge(groups, criterionKey) {
  return groups.some(
    (group) =>
      group.loopAndMergeFromQuestionConfiguration?.questionRefId ===
      criterionKey,
  );
}

export function hasCriterionLogicToCleanOnRemoval(
  audiences,
  taxonomyLogic,
  criterionKey,
) {
  return (
    hasCriterionTaxonomyLogic(taxonomyLogic, criterionKey) &&
    isTheOnlyOneCriterionOnAudiences(audiences, criterionKey)
  );
}

export function hasCriterionLoopAndMergeToCleanOnRemoval(
  audiences,
  groups,
  criterionKey,
) {
  return (
    hasCriterionBeenUsedInLoopAndMerge(groups, criterionKey) &&
    isTheOnlyOneCriterionOnAudiences(audiences, criterionKey)
  );
}

export function shouldPromptOnNestingCriterionRemoval({
  audiences,
  audienceRefId,
  taxonomyLogic,
  loopAndMergeGroups,
  criterionKey,
  isCriterionBelongsToNesting,
}) {
  const shouldPromptNestedCriterion = {
    shouldPrompt: isCriterionBelongsToNesting,
    taxonomyProperties: [],
    loopAndMergeQuestionGroups: [],
  };

  if (isEmpty(taxonomyLogic) && isEmpty(loopAndMergeGroups)) {
    return shouldPromptNestedCriterion;
  }

  let taxonomyProperties = [];
  let loopAndMergeQuestionGroups = [];

  const nestedCriterion = getNestingCriterionByAudienceRefId({
    audiences,
    audienceRefId,
    criterionKey,
  });
  if (!isEmpty(taxonomyLogic)) {
    taxonomyProperties = nestedCriterion.features
      .filter((feature) =>
        hasCriterionLogicToCleanOnRemoval(
          audiences,
          taxonomyLogic,
          feature.key,
        ),
      )
      .map((feature) => feature.key);
  }

  if (!isEmpty(loopAndMergeGroups)) {
    const criteriaUsedInLoopAndMerge = nestedCriterion.features
      .filter((feature) =>
        hasCriterionLoopAndMergeToCleanOnRemoval(
          audiences,
          loopAndMergeGroups,
          feature.key,
        ),
      )
      .map((feature) => feature.key);
    if (!isEmpty(criteriaUsedInLoopAndMerge)) {
      loopAndMergeQuestionGroups = loopAndMergeGroups.filter((group) =>
        criteriaUsedInLoopAndMerge.includes(
          group.loopAndMergeFromQuestionConfiguration?.questionRefId,
        ),
      );
    }
  }

  return {
    shouldPrompt:
      isCriterionBelongsToNesting ||
      taxonomyProperties.length > 0 ||
      loopAndMergeQuestionGroups.length > 0,
    taxonomyProperties,
    loopAndMergeQuestionGroups,
  };
}

function getUnusedTaxonomyProperties(audiences, taxonomyLogic) {
  const criteria = getAllAudiencesFlattenedCriteria(audiences);

  return Object.keys(taxonomyLogic).filter(
    (criterionKey) =>
      !criteria.some((criterion) => criterion.key === criterionKey),
  );
}

function getUnusedLoopAndMergeCriteria(audiences, loopAndMergeGroups) {
  const criteria = getAllAudiencesFlattenedCriteria(audiences);

  return loopAndMergeGroups.filter((group) => {
    const mergeConfiguration = group.loopAndMergeFromQuestionConfiguration;

    if (!mergeConfiguration) {
      return false;
    }

    if (!mergeConfiguration.isProfiling) {
      return false;
    }

    return !criteria.some(
      (criterion) => criterion.key === mergeConfiguration.questionRefId,
    );
  });
}

export function getTaxonomyPropertiesToCleanOnCountryUpdate({
  audiences,
  audienceRefId,
  updatedAudience,
  taxonomyLogic,
}) {
  if (isEmpty(taxonomyLogic)) {
    return [];
  }

  const newAudiences = audiences.map((audience) => {
    if (audience.refId === audienceRefId) {
      return {
        ...audience,
        countryId: updatedAudience.countryId,
        criteria: updatedAudience.features,
      };
    }

    return audience;
  });

  return getUnusedTaxonomyProperties(newAudiences, taxonomyLogic);
}

export function getLoopAndMergeGroupsToCleanOnCountryUpdate({
  audiences,
  audienceRefId,
  updatedAudience,
  loopAndMergeGroups,
}) {
  if (isEmpty(loopAndMergeGroups)) {
    return [];
  }

  const newAudiences = audiences.map((audience) => {
    if (audience.refId === audienceRefId) {
      return {
        ...audience,
        countryId: updatedAudience.countryId,
        criteria: updatedAudience.features || [],
      };
    }

    return audience;
  });

  return getUnusedLoopAndMergeCriteria(newAudiences, loopAndMergeGroups);
}

export function getTaxonomyPropertiesToCleanOnAudienceRemoval({
  audiences,
  audienceRefId,
  taxonomyLogic,
}) {
  if (isEmpty(taxonomyLogic)) {
    return [];
  }

  const newAudiences = audiences.filter(
    (audience) => audience.refId !== audienceRefId,
  );

  return getUnusedTaxonomyProperties(newAudiences, taxonomyLogic);
}

export function getLoopAndMergeGroupsToCleanOnAudienceRemoval({
  audiences,
  audienceRefId,
  loopAndMergeGroups,
}) {
  if (isEmpty(loopAndMergeGroups)) {
    return [];
  }

  const currentAudience = audiences.find(
    (audience) => audience.refId === audienceRefId,
  );
  const currentAudienceCriteria =
    getFlattenedCriteriaPerAudience(currentAudience);
  const removedCriteria = currentAudienceCriteria.filter(({ key }) =>
    isTheOnlyOneCriterionOnAudiences(audiences, key),
  );

  return loopAndMergeGroups.filter((group) =>
    removedCriteria.some(
      ({ key }) =>
        key === group.loopAndMergeFromQuestionConfiguration?.questionRefId,
    ),
  );
}

export function shouldPromptOnAudienceRemoval(payload) {
  const taxonomyProperties =
    getTaxonomyPropertiesToCleanOnAudienceRemoval(payload);
  const loopAndMergeQuestionGroups =
    getLoopAndMergeGroupsToCleanOnAudienceRemoval(payload);

  return {
    taxonomyProperties,
    loopAndMergeQuestionGroups,
    shouldPrompt:
      taxonomyProperties.length > 0 || loopAndMergeQuestionGroups.length > 0,
  };
}

export const enrichCriterion = ({ criterion, allCriteriaUnGrouped }) => {
  const criterionIndex = allCriteriaUnGrouped.findIndex(
    ({ key }) => key === criterion.key,
  );

  return {
    ...criterion,
    questionType: criterion.style,
    ...allCriteriaUnGrouped[criterionIndex],
    order: criterionIndex,
  };
};

export function enrichCriteria({ criteria, allCriteriaUnGrouped }) {
  if (allCriteriaUnGrouped.length > 0) {
    return criteria
      .map((criterion) => {
        if (criterion.style === QUESTION_NEST_PEEQ) {
          const features = criterion.features.map((feature) =>
            enrichCriterion({
              criterion: feature,
              allCriteriaUnGrouped,
            }),
          );

          return {
            ...criterion,
            questionType: criterion.style,
            features,
            order: features.reduce(
              (accumulator, feature) => accumulator + feature.order,
              allCriteriaUnGrouped.length,
            ),
          };
        }

        return enrichCriterion({ criterion, allCriteriaUnGrouped });
      })
      .sort((a, b) => a.order - b.order);
  }

  return [];
}

export function getNewAudienceName(items, namePrefix) {
  const itemNames = items.map((item) => item.quotaName);

  const matchItems = itemNames
    .filter((item) => {
      if (namePrefix === 'Audience') {
        return /^Audience \d$/.test(item);
      }

      return item.startsWith(namePrefix);
    })
    .sort((a, b) => a - b);

  const sortedItemNumbers = matchItems
    .map((name) => {
      if (namePrefix.length === name.length) return NaN;

      return parseInt(name[name.length - 1], 10);
    })
    .filter((num) => !Number.isNaN(num))
    .sort((a, b) => a - b);

  let nextNumber = 1;
  sortedItemNumbers.forEach((num) => {
    if (num === nextNumber) {
      nextNumber += 1;
    }
  });

  if (nextNumber === 1 && namePrefix !== 'Audience' && matchItems.length === 0)
    return namePrefix;

  return `${namePrefix} ${nextNumber}`;
}
